import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Surfaces and status display.
 *
 * The smoked-glass treatment is the shared visual language across the public
 * site and both portals - restrained translucency over near-black, with a
 * hairline border rather than a heavy shadow.
 */

export function Card({
  children,
  className,
  as: Component = 'div',
  interactive = false,
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'article' | 'section' | 'li';
  interactive?: boolean;
}) {
  return (
    <Component
      className={cn(
        'rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.07] to-white/[0.03] shadow-portal-card backdrop-blur-[6px]',
        interactive &&
          'transition-[border-color,background-color,transform] duration-200 hover:border-accent-pt/35 hover:from-white/[0.1] hover:to-white/[0.05] focus-within:border-accent-pt/40',
        className,
      )}
    >
      {children}
    </Component>
  );
}

export function CardHeader({
  title,
  description,
  action,
  className,
  headingLevel = 3,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  headingLevel?: 2 | 3 | 4;
}) {
  const Heading = `h${headingLevel}` as const;
  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 border-b border-white/8 px-5 py-4',
        className,
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <Heading className="font-condensed text-base font-semibold tracking-wide text-warm-white">
          {title}
        </Heading>
        {description && (
          <p className="text-xs leading-relaxed text-cool-steel">{description}</p>
        )}
      </div>
      {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
    </div>
  );
}

export function CardBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('px-5 py-4', className)}>{children}</div>;
}

export function CardFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-center gap-2 border-t border-white/8 px-5 py-3', className)}>
      {children}
    </div>
  );
}

export type BadgeTone =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'pt'
  | 'boxing';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'border-white/15 bg-white/[0.06] text-platinum',
  info: 'border-accent-pt/40 bg-accent-pt/12 text-accent-pt-bright',
  success: 'border-performance-green/40 bg-performance-green/12 text-performance-green',
  warning: 'border-amber-400/40 bg-amber-400/12 text-amber-300',
  danger: 'border-accent-boxing/55 bg-accent-boxing/15 text-accent-boxing-bright',
  pt: 'border-accent-pt/40 bg-accent-pt/12 text-accent-pt-bright',
  boxing: 'border-accent-boxing/55 bg-accent-boxing/15 text-accent-boxing-bright',
};

export function Badge({
  children,
  tone = 'neutral',
  className,
  /** Prefixed to the visible text for screen readers, e.g. "Status: ". */
  srPrefix,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
  srPrefix?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
        BADGE_TONES[tone],
        className,
      )}
    >
      {srPrefix && <span className="sr-only">{srPrefix}</span>}
      {children}
    </span>
  );
}

/**
 * Status indicator. The tone is always accompanied by text, never colour alone,
 * so it satisfies WCAG 1.4.1.
 */
export function StatusPill({
  label,
  tone = 'neutral',
  className,
}: {
  label: string;
  tone?: BadgeTone;
  className?: string;
}) {
  return (
    <Badge tone={tone} srPrefix="Status: " className={className}>
      <span
        aria-hidden="true"
        className={cn(
          'size-1.5 rounded-full',
          tone === 'success' && 'bg-performance-green',
          tone === 'danger' && 'bg-accent-boxing',
          tone === 'warning' && 'bg-amber-300',
          tone === 'info' && 'bg-accent-pt',
          tone === 'pt' && 'bg-accent-pt',
          tone === 'boxing' && 'bg-accent-boxing',
          tone === 'neutral' && 'bg-cool-steel',
        )}
      />
      {label}
    </Badge>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
  className,
}: {
  label: string;
  /** Pass a string. `null` renders an explicit "no data" state, not a zero. */
  value: string | null;
  hint?: ReactNode;
  tone?: BadgeTone;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <dt className="text-[0.7rem] font-medium tracking-[0.14em] text-accent-pt-bright/80 uppercase">
        {label}
      </dt>
      <dd
        className={cn(
          'flex flex-col gap-1 font-condensed text-3xl leading-none font-semibold tabular-nums tracking-tight',
          value === null ? 'text-cool-steel' : 'text-warm-white',
          tone === 'success' && value !== null && 'text-performance-green',
          tone === 'danger' && value !== null && 'text-accent-boxing',
          tone === 'info' && value !== null && 'text-accent-pt-bright',
        )}
      >
        {value ?? <span className="text-base font-normal">Not recorded</span>}
        {hint && <span className="text-xs leading-relaxed font-normal text-cool-steel">{hint}</span>}
      </dd>
    </div>
  );
}

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const ALERT_TONES: Record<AlertTone, string> = {
  info: 'border-accent-pt/40 bg-accent-pt/8',
  success: 'border-performance-green/40 bg-performance-green/8',
  warning: 'border-amber-400/40 bg-amber-400/8',
  danger: 'border-accent-boxing/45 bg-accent-boxing/8',
};

export function Alert({
  tone = 'info',
  title,
  children,
  action,
  className,
  /** `true` for errors that must interrupt; otherwise announced politely. */
  assertive = false,
}: {
  tone?: AlertTone;
  title?: string;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
  assertive?: boolean;
}) {
  return (
    <div
      role={tone === 'danger' || assertive ? 'alert' : 'status'}
      className={cn('rounded-md border p-4', ALERT_TONES[tone], className)}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          {title && <p className="text-sm font-semibold text-warm-white">{title}</p>}
          <div className="text-sm leading-relaxed text-platinum">{children}</div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
}

/**
 * Empty state. Always explains *why* it is empty and offers the next action, so
 * a new client never sees a blank panel and wonders whether it is broken.
 */
export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: string;
  description: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-2xl border border-dashed border-accent-pt/25 bg-gradient-to-b from-accent-pt/[0.06] to-transparent px-6 py-10 text-center',
        className,
      )}
    >
      {icon && (
        <span aria-hidden="true" className="text-accent-pt-bright/70">
          {icon}
        </span>
      )}
      <p className="font-condensed text-base font-semibold tracking-wide text-warm-white">{title}</p>
      <p className="max-w-prose text-sm leading-relaxed text-platinum/75">{description}</p>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

export function Avatar({
  name,
  initials: suppliedInitials,
  src,
  size = 'md',
  className,
}: {
  /** Full name, from which initials are derived when none are supplied. */
  name?: string;
  /** Pre-computed initials, for callers that already display a name elsewhere. */
  initials?: string;
  src?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const dimension =
    size === 'xs' ? 'size-6 text-[10px]' : size === 'sm' ? 'size-8 text-xs' : size === 'lg' ? 'size-14 text-lg' : 'size-10 text-sm';

  const initials =
    suppliedInitials ??
    (name ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');

  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={cn('shrink-0 rounded-full object-cover', dimension, className)}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full bg-graphite font-semibold text-platinum',
        dimension,
        className,
      )}
    >
      {initials || '?'}
    </span>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  breadcrumbs,
  className,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('flex flex-col gap-3', className)}>
      {breadcrumbs}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-1.5 hidden h-9 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-accent-pt to-performance-green sm:block"
          />
          <div className="flex min-w-0 flex-col gap-1.5">
            <h1 className="text-portal-title text-2xl text-warm-white sm:text-4xl">{title}</h1>
            {description && (
              <p className="max-w-prose text-sm leading-relaxed text-platinum/80">{description}</p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:shrink-0">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}

/**
 * Definition list used throughout the client workspace. Semantic `<dl>` so the
 * label/value relationship is exposed rather than implied by layout.
 */
export function DescriptionList({
  items,
  columns = 2,
  className,
}: {
  items: { label: string; value: ReactNode }[];
  columns?: 1 | 2 | 3;
  className?: string;
}) {
  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-4',
        columns === 1 && 'grid-cols-1',
        columns === 2 && 'grid-cols-1 sm:grid-cols-2',
        columns === 3 && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        className,
      )}
    >
      {items.map((item) => (
        <div key={item.label} className="flex flex-col gap-1">
          <dt className="text-xs font-medium tracking-wide text-cool-steel uppercase">
            {item.label}
          </dt>
          <dd className="text-sm leading-relaxed text-warm-white">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
